import {EvaluationCriteriaWithScores} from '@frontend/shared-api';

export type RawAnswers = Map<number, number | null>;

export const INDIVIDUAL_RESULT_SC_NUMBERS = {
  finance: 11,
  company: 12,
  culture: 13,
  references: 14,
} as const;

export const FINANCE_SCA = {
  equityRatio: 111,
  researchRatio: 112,
  marketYears: 113,
  liabilityInsurance: 114,
  recurringRevenueShare: 117,
  largestCustomerShare: 118,
  cloudRevenue: [1153, 1152, 1151],
  ebitda: [1163, 1162, 1161],
  revenue: [1192, 1191, 1193],
} as const;

export const COMPANY_SCA = {
  technologyPartners: 121,
  implementationPartners: 122,
  iso27001: 124,
  dataCentersInEea: 125,
  activeCustomers: 126,
  grossRevenueRetention: 127,
  hrCloudEmployees: 1230,
  development: 1231,
  operations: 1232,
  customerSupport: 1233,
  employees: [1283, 1282, 1281],
} as const;

export const CULTURE_SCA = {
  values: 131,
  strategy: 132,
  whistleblowing: 133,
  pendingLitigation: 134,
  customerSatisfaction: 135,
  socialProjects: 136,
  esgReporting: 137,
  compliancePolicies: 138,
  codeOfConduct: 139,
} as const;

export const REFERENCE_SCA = [141, 142, 143, 144, 145, 146, 147, 148] as const;

export const FINANCE_ANSWER_SCA: number[] = [
  FINANCE_SCA.equityRatio,
  FINANCE_SCA.researchRatio,
  FINANCE_SCA.marketYears,
  FINANCE_SCA.liabilityInsurance,
  FINANCE_SCA.recurringRevenueShare,
  FINANCE_SCA.largestCustomerShare,
  ...FINANCE_SCA.cloudRevenue,
  ...FINANCE_SCA.ebitda,
  ...FINANCE_SCA.revenue,
];

export const COMPANY_ANSWER_SCA: number[] = [
  COMPANY_SCA.technologyPartners,
  COMPANY_SCA.implementationPartners,
  COMPANY_SCA.iso27001,
  COMPANY_SCA.dataCentersInEea,
  COMPANY_SCA.activeCustomers,
  COMPANY_SCA.grossRevenueRetention,
  COMPANY_SCA.hrCloudEmployees,
  COMPANY_SCA.development,
  COMPANY_SCA.operations,
  COMPANY_SCA.customerSupport,
  ...COMPANY_SCA.employees,
];

export const CULTURE_ANSWER_SCA: number[] = Object.values(CULTURE_SCA);

export interface YearFigures {
  yearsAgo: number;
  revenue: number | null;
  cloudRevenue: number | null;
  ebitda: number | null;
}

export interface FinanceKpis {
  equityRatio: number | null;
  researchRatio: number | null;
  marketYears: number | null;
  liabilityInsurance: boolean | null;
  recurringRevenueShare: number | null;
  largestCustomerShare: number | null;
  revenueGrowth: number | null;
  ebitdaMargin: number | null;
  cloudRevenueShare: number | null;
  years: YearFigures[];
}

export interface CompanyKpis {
  activeCustomers: number | null;
  grossRevenueRetention: number | null;
  hrCloudEmployees: number | null;
  employeeGrowth: number | null;
  implementationPartners: number | null;
  technologyPartners: number | null;
  iso27001: boolean | null;
  dataCentersInEea: boolean | null;
  development: number | null;
  operations: number | null;
  customerSupport: number | null;
  employees: (number | null)[];
}

export interface AnswerCompleteness {
  answered: number;
  total: number;
  missingScaNumbers: number[];
}

export function providerRawAnswers(
  criteria: EvaluationCriteriaWithScores[],
  hrToolDirectusId: string,
): RawAnswers {
  return new Map(
    criteria
      .flatMap((criterion) => criterion.subCriteria)
      .flatMap((subCriterion) => subCriterion.attributes)
      .map((attribute) => {
        const answer = attribute.scores.find(
          (score) => score.hrToolDirectusId === hrToolDirectusId,
        );
        return [attribute.scaNumber, answer && !answer.scoreAbsent ? answer.score : null];
      }),
  );
}

export function financeKpis(answers: RawAnswers): FinanceKpis {
  const revenue = FINANCE_SCA.revenue.map((sca) => answer(answers, sca));
  const cloudRevenue = FINANCE_SCA.cloudRevenue.map((sca) => answer(answers, sca));
  const ebitda = FINANCE_SCA.ebitda.map((sca) => answer(answers, sca));
  const lastYear = revenue.length - 1;

  return {
    equityRatio: percent(answers, FINANCE_SCA.equityRatio),
    researchRatio: percent(answers, FINANCE_SCA.researchRatio),
    marketYears: answer(answers, FINANCE_SCA.marketYears),
    liabilityInsurance: yesNo(answers, FINANCE_SCA.liabilityInsurance),
    recurringRevenueShare: percent(answers, FINANCE_SCA.recurringRevenueShare),
    largestCustomerShare: percent(answers, FINANCE_SCA.largestCustomerShare),
    revenueGrowth: compoundGrowth(revenue[0], revenue[lastYear], lastYear),
    ebitdaMargin: ratio(ebitda[lastYear], revenue[lastYear]),
    cloudRevenueShare: ratio(cloudRevenue[lastYear], revenue[lastYear]),
    years: revenue.map((yearRevenue, index) => ({
      yearsAgo: lastYear - index,
      revenue: yearRevenue,
      cloudRevenue: cloudRevenue[index],
      ebitda: ebitda[index],
    })),
  };
}

export function companyKpis(answers: RawAnswers): CompanyKpis {
  const employees = COMPANY_SCA.employees.map((sca) => answer(answers, sca));
  const lastYear = employees.length - 1;

  return {
    activeCustomers: answer(answers, COMPANY_SCA.activeCustomers),
    grossRevenueRetention: percent(answers, COMPANY_SCA.grossRevenueRetention),
    hrCloudEmployees: answer(answers, COMPANY_SCA.hrCloudEmployees),
    employeeGrowth: compoundGrowth(employees[0], employees[lastYear], lastYear),
    implementationPartners: answer(answers, COMPANY_SCA.implementationPartners),
    technologyPartners: answer(answers, COMPANY_SCA.technologyPartners),
    iso27001: yesNo(answers, COMPANY_SCA.iso27001),
    dataCentersInEea: yesNo(answers, COMPANY_SCA.dataCentersInEea),
    development: answer(answers, COMPANY_SCA.development),
    operations: answer(answers, COMPANY_SCA.operations),
    customerSupport: answer(answers, COMPANY_SCA.customerSupport),
    employees,
  };
}

export function answerCompleteness(answers: RawAnswers, scaNumbers: number[]): AnswerCompleteness {
  const missingScaNumbers = scaNumbers.filter((sca) => answer(answers, sca) === null);

  return {
    answered: scaNumbers.length - missingScaNumbers.length,
    total: scaNumbers.length,
    missingScaNumbers,
  };
}

export function answer(answers: RawAnswers, scaNumber: number): number | null {
  return answers.get(scaNumber) ?? null;
}

export function percent(answers: RawAnswers, scaNumber: number): number | null {
  const value = answer(answers, scaNumber);
  return value === null ? null : value * 100;
}

export function yesNo(answers: RawAnswers, scaNumber: number): boolean | null {
  const value = answer(answers, scaNumber);
  return value === null ? null : value >= 1;
}

function compoundGrowth(
  first: number | null,
  last: number | null,
  intervals: number,
): number | null {
  if (first === null || last === null || first <= 0 || last < 0) {
    return null;
  }
  return ((last / first) ** (1 / intervals) - 1) * 100;
}

function ratio(part: number | null, whole: number | null): number | null {
  return part === null || whole === null || whole === 0 ? null : (part / whole) * 100;
}
